import { useLocation, Link } from "react-router-dom";
import { categoryGuides } from "@/mocks/articleContent";
import JsonLd from "@/components/base/JsonLd";
import { getArticleSchema, getFAQPageSchema, getBreadcrumbSchema } from "@/lib/schema";

function getCategoryFromPath(pathname: string): string | undefined {
  const match = pathname.match(/\/([a-z]+)-spreadsheet-guide/);
  return match ? match[1] : undefined;
}

const heroImages: Record<string, string> = {
  shoes: "https://readdy.ai/api/search-image?query=Professional%20product%20photography%20of%20premium%20sneakers%20and%20leather%20dress%20shoes%20arranged%20on%20warm%20terracotta%20concrete%20surface%2C%20soft%20natural%20morning%20sunlight%20casting%20gentle%20shadows%2C%20editorial%20fashion%20still%20life%20with%20organic%20linen%20fabric%20texture%20backdrop%2C%20minimalist%20Scandinavian%20aesthetic%2C%20warm%20neutral%20earth%20tone%20color%20palette%2C%20clean%20harmonious%20composition%20with%20elegant%20negative%20space&width=1600&height=800&seq=shoes-hero-2026x&orientation=landscape",
  hoodies: "https://readdy.ai/api/search-image?query=Lifestyle%20product%20photography%20of%20premium%20oversized%20hoodies%20in%20warm%20cream%20sand%20and%20charcoal%20tones%20draped%20artistically%20on%20wooden%20hangers%2C%20soft%20diffused%20afternoon%20light%20through%20sheer%20curtains%2C%20textured%20knit%20fabric%20details%20visible%2C%20cozy%20minimalist%20aesthetic%20with%20warm%20neutral%20palette%2C%20editorial%20fashion%20flat%20lay%20style%2C%20organic%20cotton%20texture%20emphasis&width=1600&height=800&seq=hoodies-hero-2026x&orientation=landscape",
  tshirts: "https://readdy.ai/api/search-image?query=Elegant%20product%20photography%20of%20premium%20cotton%20t-shirts%20in%20cream%20white%20sand%20and%20olive%20tones%20neatly%20folded%20on%20natural%20linen%20fabric%20surface%2C%20soft%20morning%20sunlight%20from%20window%20creating%20gentle%20highlights%2C%20minimalist%20wardrobe%20essentials%20aesthetic%2C%20warm%20neutral%20earth%20color%20palette%2C%20editorial%20fashion%20photography%20with%20organic%20texture%2C%20clean%20refined%20composition&width=1600&height=800&seq=tshirts-hero-2026x&orientation=landscape",
  jackets: "https://readdy.ai/api/search-image?query=Stylish%20product%20photography%20of%20premium%20bomber%20jacket%20denim%20jacket%20and%20leather%20jacket%20displayed%20on%20minimalist%20wooden%20rack%2C%20warm%20golden%20hour%20backlighting%20through%20industrial%20windows%2C%20textured%20fabric%20details%20emphasized%2C%20editorial%20fashion%20aesthetic%20with%20warm%20sand%20and%20taupe%20tones%2C%20clean%20composition%20with%20organic%20shadows&width=1600&height=800&seq=jackets-hero-2026x&orientation=landscape",
  accessories: "https://readdy.ai/api/search-image?query=Artistic%20product%20photography%20of%20elegant%20watches%20sunglasses%20and%20minimalist%20jewelry%20pieces%20arranged%20on%20smooth%20warm%20sand%20colored%20marble%20surface%2C%20soft%20diffused%20daylight%20creating%20subtle%20reflections%2C%20luxury%20lifestyle%20aesthetic%20with%20warm%20cream%20and%20beige%20palette%2C%20editorial%20still%20life%20composition%20with%20elegant%20negative%20space&width=1600&height=800&seq=accessories-hero-2026x&orientation=landscape",
  bags: "https://readdy.ai/api/search-image?query=Sophisticated%20product%20photography%20of%20premium%20leather%20backpack%20canvas%20tote%20bag%20and%20messenger%20bag%20displayed%20on%20warm%20wooden%20table%2C%20soft%20natural%20light%20from%20large%20window%2C%20rich%20textured%20leather%20and%20canvas%20details%20highlighted%2C%20minimalist%20aesthetic%20with%20warm%20neutral%20olive%20and%20sand%20tones%2C%20editorial%20photography%20with%20harmonious%20composition&width=1600&height=800&seq=bags-hero-2026x&orientation=landscape",
};

export default function CategoryGuidePage() {
  const location = useLocation();
  const category = getCategoryFromPath(location.pathname);
  const guide = category ? categoryGuides[category] : undefined;

  if (!guide) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center px-4">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">Guide Not Found</h1>
          <p className="text-foreground-600 mb-6">The guide you are looking for does not exist yet.</p>
          <Link to="/" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-md text-sm font-semibold transition-all duration-200 cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Back to Homepage
          </Link>
        </div>
      </div>
    );
  }

  const heroImage = heroImages[guide.slug] || "";

  const guideUrl = `https://hipobuyspread.online/${guide.slug}-spreadsheet-guide`;

  const articleSchema = getArticleSchema({
    headline: guide.h1,
    description: guide.description,
    url: guideUrl,
    datePublished: "2026-07-05",
    dateModified: "2026-07-11",
  });
  const faqSchema = getFAQPageSchema(
    guide.faqs.map((f) => ({ question: f.q, answer: f.a }))
  );
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Home", url: "https://hipobuyspread.online/" },
    {
      name: guide.slug.charAt(0).toUpperCase() + guide.slug.slice(1) + " Guide",
      url: guideUrl,
    },
  ]);

  return (
    <>
      <JsonLd data={articleSchema} />
      <JsonLd data={faqSchema} />
      <JsonLd data={breadcrumbSchema} />
      <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="font-heading text-xl md:text-2xl font-bold text-foreground-900 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Hipobuy Sheet
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="hidden sm:flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-home-line"></i>
              Home
            </Link>
            <Link
              to="/hipobuy-spreadsheet-guide"
              className="hidden sm:flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-guide-line"></i>
              Main Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        </div>
      </header>

      <main>
        <article>
          <header className="relative w-full min-h-[420px] md:min-h-[500px] flex items-center justify-center overflow-hidden">
            {heroImage && (
              <>
                <div className="absolute inset-0">
                  <img
                    src={heroImage}
                    alt={`${guide.title} - Hipobuy Spreadsheet product photography`}
                    title={guide.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/35"></div>
              </>
            )}
            <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-16">
              <div className="inline-flex items-center gap-2 text-xs font-medium text-accent-100 bg-accent-500/30 backdrop-blur-sm px-3 py-1.5 rounded-full mb-5">
                <i className={guide.icon}></i>
                Category Guide
              </div>
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                {guide.h1}
              </h1>
              <p className="text-white/80 text-sm md:text-base max-w-2xl mx-auto">
                {guide.description}
              </p>
            </div>
          </header>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="prose-custom space-y-10">
              {guide.sections.map((section, idx) => (
                <section key={idx}>
                  <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((p, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-foreground-700 leading-relaxed text-sm md:text-base mb-4"
                    >
                      {p}
                    </p>
                  ))}
                </section>
              ))}

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-5">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {guide.faqs.map((faq, idx) => (
                    <div key={idx} className="bg-background-50 rounded-lg p-5 border border-background-200/60">
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                        {faq.q}
                      </h3>
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                  Explore More Hipobuy Spreadsheet Guides
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {Object.values(categoryGuides)
                    .filter((g) => g.slug !== guide.slug)
                    .map((related) => (
                      <Link
                        key={related.slug}
                        to={`/${related.slug}-spreadsheet-guide`}
                        className="flex items-center gap-2.5 p-3.5 bg-background-50 border border-background-200/60 rounded-lg hover:border-primary-300 hover:bg-background-100 transition-all duration-200 cursor-pointer"
                      >
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100/80 text-accent-600 flex-shrink-0">
                          <i className={`${related.icon} text-sm`}></i>
                        </div>
                        <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">
                          {related.slug.charAt(0).toUpperCase() + related.slug.slice(1)} Guide
                        </span>
                      </Link>
                    ))}
                </div>
              </section>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6 border-t border-background-200/60">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full sm:w-auto bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3.5 rounded-md text-base font-semibold transition-all duration-200 text-center whitespace-nowrap cursor-pointer"
                >
                  Browse {guide.slug.charAt(0).toUpperCase() + guide.slug.slice(1)} on Hipobuy Sheet
                </a>
                <Link
                  to="/"
                  className="w-full sm:w-auto bg-background-100 hover:bg-background-200 text-foreground-800 px-8 py-3.5 rounded-md text-base font-semibold transition-all duration-200 text-center whitespace-nowrap cursor-pointer border border-background-200/70"
                >
                  <i className="ri-home-line mr-2"></i>
                  Back to Homepage
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>

      <footer className="bg-background-100 border-t border-background-200/70 py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <p className="text-xs text-foreground-500">
            &copy; {new Date().getFullYear()} Hipobuy Spreadsheet Guide. Independent resource website.{" "}
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
              Return to Homepage
            </Link>
          </p>
        </div>
      </footer>
    </>
  );
}